import { useState, useEffect } from "react";
import { User, LogOut, ShoppingBag, Heart, MapPin, Gift, ChevronRight } from "lucide-react";
import { useRouter } from "next/router";
import { Button } from "@/components/v2/ui/button";
import { getCookieItem, removeCookieItem } from "@/utils/utils";
import { getUserDetails } from "@/pages/api/index";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/v2/ui/accordion";
import { cn } from "@/lib/utils";

const UserMenuMobile = ({ onClose, mode = "inline" }) => {
    const router = useRouter();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const checkAuth = async () => {
            const token = getCookieItem("accessToken");
            if (token) {
                try {
                    const response = await getUserDetails();
                    if (response && response.status === 200) {
                        setUserData(response.data.body);
                        setIsLoggedIn(true);
                    } else {
                        removeCookieItem("accessToken");
                        setIsLoggedIn(false);
                    }
                } catch (error) {
                    console.error("Auth check failed", error);
                    setIsLoggedIn(false);
                }
            } else {
                setIsLoggedIn(false);
            }
        };

        checkAuth();
    }, []);

    const handleLogout = () => {
        removeCookieItem("accessToken");
        setIsLoggedIn(false);
        setUserData(null);
        if (onClose) onClose();
        router.push("/");
    };

    const navigateTo = (path) => {
        if (onClose) onClose();
        router.push(path);
    };

    if (!isLoggedIn) {
        return (
            <div className="p-4 border-b bg-muted/30">
                <p className="text-xs font-semibold text-muted-foreground mb-3 px-1 uppercase tracking-wider">Account</p>
                <Button
                    variant="default"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 rounded-xl flex items-center justify-center gap-2"
                    onClick={() => navigateTo(`/auth?redirect=${encodeURIComponent(router.asPath)}`)}
                >
                    <User className="h-5 w-5" />
                    Login / Sign Up
                </Button>
            </div>
        );
    }

    const menuItems = [
        { icon: User, label: "My Profile", path: "/user/profile" },
        { icon: ShoppingBag, label: "My Orders", path: "/user/orders" },
        { icon: Heart, label: "Wishlist", path: "/user/wishlist" },
        { icon: MapPin, label: "Manage Addresses", path: "/user/address" },
        { icon: Gift, label: "Blessing Points", path: "/user/blessing-points" },
    ];

    const MenuContent = () => (
        <div className="py-1">
            <div className="space-y-0.5">
                {menuItems.map((item, index) => (
                    <button
                        key={index}
                        onClick={() => navigateTo(item.path)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors text-sm font-medium"
                    >
                        <div className="flex items-center gap-3">
                            <item.icon className="h-4 w-4 text-primary" />
                            <span>{item.label}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </button>
                ))}
                
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-destructive/5 text-destructive transition-colors text-sm font-medium mt-1"
                >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                </button>
            </div>
        </div>
    );

    const UserHeader = ({ isAccordion = false }) => (
        <div className={cn("flex items-center gap-3 w-full text-left", !isAccordion && "p-4 bg-primary/5 border-b border-primary/10")}>
            <div className="h-10 w-10 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                {userData?.profileImage ? (
                    <img src={userData.profileImage} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                    <img src="/icons/acount.svg" alt="Profile Placeholder" className="h-full w-full object-cover" />
                )}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold text-foreground truncate">{userData?.userName || userData?.name || "My Account"}</span>
                <span className="text-[10px] text-muted-foreground truncate">{userData?.email || userData?.phone}</span>
            </div>
        </div>
    );

    if (mode === "accordion") {
        return (
            <Accordion type="single" collapsible className="w-full border-b">
                <AccordionItem value="user-menu" className="border-none">
                    <AccordionTrigger className="px-4 hover:no-underline py-4">
                        <UserHeader isAccordion={true} />
                    </AccordionTrigger>
                    <AccordionContent className="p-0 border-t border-muted">
                        <MenuContent />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        );
    }

    return (
        <div className="border-b">
            <UserHeader />
            <div className="py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-2 mb-1 px-4">My Account</p>
                <MenuContent />
            </div>
        </div>
    );
};

export default UserMenuMobile;
